import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { FiCheck, FiMail } from 'react-icons/fi';
import { Footer, SEO } from '@/components';
import { track } from '@/lib/analytics';
import { API_BASE_URL } from '@/lib/api';
import { requireDivorceProduct } from '@/lib/divorce-gate';
import { DOC_PREP_DISCLAIMER, SERVER_INSTRUCTIONS } from '@/lib/divorce-legal';

export const Route = createFileRoute('/divorce/server')({
  beforeLoad: async () => {
    await requireDivorceProduct();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: DivorceServerPage,
});

function DivorceServerPage() {
  const { token } = Route.useSearch();
  const [servedOn, setServedOn] = useState('');
  const [method, setMethod] = useState('mail');
  const [complete, setComplete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const confirm = async () => {
    if (!token || !servedOn) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/v1/divorce/server/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, served_on: servedOn, method }),
      });
      if (!response.ok) throw new Error('Could not confirm service.');
      setComplete(true);
      track('Disclosure Service Confirmed', { method });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not confirm service.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Confirm disclosure service'
        description='Instructions for the adult serving California financial disclosures.'
        path='/divorce/server'
      />
      <main className='mx-auto max-w-xl px-4 py-12'>
        {complete ? (
          <section className='rounded-2xl bg-emerald-50 p-6 text-emerald-950'>
            <FiCheck className='h-8 w-8' />
            <h1 className='mt-3 text-2xl font-extrabold'>Service confirmed</h1>
            <p className='mt-2'>
              Harbor saved the service date. The spouses can use it when they prepare
              their FL-141 proofs.
            </p>
          </section>
        ) : (
          <>
            <FiMail className='h-8 w-8 text-primary' />
            <h1 className='mt-3 text-3xl font-extrabold text-gray-900'>
              Serve the disclosure packet
            </h1>
            <p className='mt-3 text-gray-700'>{SERVER_INSTRUCTIONS}</p>
            <ol className='mt-6 list-decimal space-y-3 pl-5 text-gray-700'>
              <li>Use the complete packet the spouse gave you.</li>
              <li>Mail it to the other spouse. Do not mail it to the court.</li>
              <li>Record the date and method below after you send it.</li>
            </ol>
            <div className='mt-7 grid gap-3 sm:grid-cols-2'>
              <label className='text-sm font-semibold text-gray-700'>
                Date served
                <input
                  className='mt-1 w-full rounded-lg border px-3 py-2 font-normal'
                  type='date'
                  value={servedOn}
                  onChange={(event) => setServedOn(event.target.value)}
                />
              </label>
              <label className='text-sm font-semibold text-gray-700'>
                Method
                <select
                  className='mt-1 w-full rounded-lg border px-3 py-2 font-normal'
                  value={method}
                  onChange={(event) => setMethod(event.target.value)}
                >
                  <option value='mail'>Mail</option>
                  <option value='personal'>Personal delivery</option>
                </select>
              </label>
            </div>
            <button
              type='button'
              disabled={!token || !servedOn || busy}
              onClick={() => void confirm()}
              className='mt-5 w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white disabled:opacity-50'
            >
              {busy ? 'Saving…' : 'Confirm service'}
            </button>
            {!token ? (
              <p className='mt-3 text-sm font-medium text-red-600'>
                This invite link is missing its token.
              </p>
            ) : null}
            {error ? <p className='mt-3 text-sm font-medium text-red-600'>{error}</p> : null}
          </>
        )}
        <p className='mt-10 text-xs text-gray-500'>{DOC_PREP_DISCLAIMER}</p>
      </main>
      <Footer />
    </div>
  );
}
